import { ASSESS_CANDIDATE, GENERATE_CANDIDATE_EMAIL_MESSAGES } from "@lib/graphql/mutations";
import { ClipboardCheck, Loader2, Mail } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useMutation } from "urql";

interface CandidateActionButtonsProps {
  campaignId: string;
  campaignCandidateId: string;
  candidateId: string;
  candidateName: string;
  candidateRole?: string;
  candidateCompany?: string;
}

export function CandidateActionButtons({
  campaignId,
  campaignCandidateId,
  candidateId,
}: CandidateActionButtonsProps) {
  const navigate = useNavigate();
  const [{ fetching }, assessCandidate] = useMutation(ASSESS_CANDIDATE);
  const [{ fetching: drafting }, generateEmail] = useMutation(GENERATE_CANDIDATE_EMAIL_MESSAGES);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(false), 2000);
    return () => clearTimeout(timer);
  }, [error]);

  // Queue the email draft and open the Outreach workspace on this candidate
  // (#3169). A hand-edited current draft is not overwritten from here: the
  // workspace's Regenerate asks first, so we just navigate.
  const handleDraftEmail = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      const result = await generateEmail({
        input: { campaignId, campaignCandidateIds: [campaignCandidateId] },
      });
      const payload = result.data?.generateCandidateEmailMessages;
      if (payload?.errors?.length || result.error) {
        setError(true);
        return;
      }
      navigate(`/campaigns/${campaignId}/run/outreach?cc=${campaignCandidateId}&channel=email`);
    },
    [campaignCandidateId, campaignId, generateEmail, navigate],
  );

  const handleAssess = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      const result = await assessCandidate({
        input: { campaignId, candidateId },
      });
      const payload = result.data?.assessCandidate;
      if (payload?.errors?.length || result.error) {
        setError(true);
      }
    },
    [assessCandidate, campaignId, candidateId],
  );

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        title="Draft outreach email"
        disabled={drafting}
        onClick={handleDraftEmail}
        className={`rounded p-1.5 ${
          error
            ? "text-red-500"
            : drafting
              ? "text-text-faint opacity-50"
              : "text-text-faint hover:bg-card-hover hover:text-text-secondary"
        }`}
      >
        {drafting ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
      </button>
      <button
        type="button"
        title="Assess"
        disabled={fetching}
        onClick={handleAssess}
        className={`rounded p-1.5 ${
          error
            ? "text-red-500"
            : fetching
              ? "text-text-faint opacity-50"
              : "text-text-faint hover:bg-card-hover hover:text-text-secondary"
        }`}
      >
        {fetching ? <Loader2 size={14} className="animate-spin" /> : <ClipboardCheck size={14} />}
      </button>
    </div>
  );
}
